import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import gsap from 'gsap';
import { getStrapiMediaAttributes, submitLead } from '../../lib/strapi';
import { getContentFontStyle, getHeadingFontStyle } from '../../lib/contentBlockFontSize';
import { useGlobal } from '../../context/GlobalContext';
import { resolveLegalLink } from '../../lib/legalLinks';
import { getLocalizedCountryOptions } from '../../lib/countries';
import { isEnglishLocale } from '../../lib/locale';
import { getIconFallbackLabel, getUiText } from '../../lib/uiText';
import styles from './ContactForm.module.css';

/* ─── Country codes for phone field ─── */
const COUNTRY_CODES = [
  { code: '+52', flag: '🇲🇽', label: 'MX' },
  { code: '+1', flag: '🇺🇸', label: 'US' },
  { code: '+57', flag: '🇨🇴', label: 'CO' },
  { code: '+34', flag: '🇪🇸', label: 'ES' },
  { code: '+54', flag: '🇦🇷', label: 'AR' },
  { code: '+56', flag: '🇨🇱', label: 'CL' },
  { code: '+55', flag: '🇧🇷', label: 'BR' },
  { code: '+51', flag: '🇵🇪', label: 'PE' },
];

const normalizeProjectId = (value = '') => {
  const normalized = String(value || '').trim().replace(/\s+/g, '_');
  return normalized || undefined;
};

const normalizeUnitLookupValue = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, '')
  .toLowerCase()
  .trim();

const parseLeadEstimatedValue = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;

  const numericGroups = raw.match(/\d[\d.,]*/g) || [];
  if (numericGroups.length !== 1) return undefined;

  let normalized = numericGroups[0];
  const commaCount = (normalized.match(/,/g) || []).length;
  const dotCount = (normalized.match(/\./g) || []).length;

  if (commaCount > 0 && dotCount > 0) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.').replace(/,/g, '');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (commaCount > 0) {
    if (commaCount > 1) {
      normalized = normalized.replace(/,/g, '');
    } else {
      const [left = '', right = ''] = normalized.split(',');
      normalized = right.length > 0 && right.length <= 2 ? `${left}.${right}` : `${left}${right}`;
    }
  } else if (dotCount > 1) {
    normalized = normalized.replace(/\./g, '');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const hasRequiredFieldValue = (field, values) => {
  if (!field?.required) return true;

  const key = typeof field?.name === 'string' ? field.name.trim() : '';
  if (!key) return true;

  const value = values[key];

  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim());
  }

  return String(value ?? '').trim().length > 0;
};

const getFirstMissingRequiredField = (fields, values) => (
  (fields || []).find((field) => !hasRequiredFieldValue(field, values)) || null
);

const getFieldValidationMessage = (field, values, messages = {}) => {
  const key = typeof field?.name === 'string' ? field.name.trim() : '';
  if (!key) return '';

  const rawValue = values[key];
  const value = Array.isArray(rawValue) ? rawValue.join(' ').trim() : String(rawValue ?? '').trim();

  if (field?.required && !value) {
    return messages.errorRequired || '';
  }

  if (field?.type === 'email' && value) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return messages.errorEmail || '';
    }
  }

  return '';
};

const validateFields = (fields, values, messages = {}) => (fields || []).reduce((accumulator, field) => {
  const message = getFieldValidationMessage(field, values, messages);
  if (message && field?.name) {
    accumulator[field.name] = message;
  }
  return accumulator;
}, {});

const CONTACT_FORM_HEADING_FONT_SIZE_FALLBACKS = {
  h1: '2em',
  h2: '1.5em',
  h3: '1.1em',
};
const CONTACT_FORM_PARAGRAPH_FONT_SIZE = '0.95em';
const CONTACT_FORM_LIST_FONT_SIZE = '0.95em';

const hasRenderableContentBlock = (block) => {
  if (!block) return false;

  switch (block.__component) {
    case 'shared.heading':
    case 'shared.paragraph':
      return Boolean(String(block.text || '').trim());
    case 'shared.list':
      return Array.isArray(block.items) && block.items.some((item) => String(item?.text || '').trim());
    default:
      return false;
  }
};

const ContactForm = ({ data, source, pageProjectId = '', items = [] }) => {
  const { legalRouteMap, companyInfo, locale } = useGlobal();
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const formRef = useRef(null);
  const thankYouRef = useRef(null);
  const successOverlayRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const [formValues, setFormValues] = useState({});
  const [phoneCountry, setPhoneCountry] = useState(COUNTRY_CODES[0]);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const backgroundImage = data?.backgroundImage?.url
    ? getStrapiMediaAttributes(data.backgroundImage, {
      altFallback: data?.overlayTitle || data?.overlaySubtitle || '',
      titleFallback: data?.overlayTitle || data?.overlaySubtitle || '',
      preferredFormats: ['large', 'medium', 'small'],
    }).src
    : null;
  const hasHero = Boolean(backgroundImage || data?.overlayTitle || data?.overlaySubtitle);
  const fields = data?.fields || [];
  const localizedCountryOptions = getLocalizedCountryOptions(locale);
  const phoneCountryAriaLabel = isEnglishLocale(locale) ? 'Country code' : 'Código de país';
  const derivedProjectId = normalizeProjectId(
    data?.projectId || pageProjectId || companyInfo?.companyName || import.meta.env.VITE_DEFAULT_PROJECT_ID
  );

  /* ─── URL query param for pre-filled unit ─── */
  const [searchParams] = useSearchParams();
  const unidadParam = searchParams.get('unidad') || searchParams.get('unit') || '';
  const matchedUnidadItem = items.find(
    (item) => normalizeUnitLookupValue(item?.titulo) === normalizeUnitLookupValue(unidadParam)
  ) || null;
  const lockedUnidadValue = matchedUnidadItem?.titulo || '';

  /* ─── Animation ─── */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const animItems = card.querySelectorAll(`.${styles.contactForm__animateItem}`);
    gsap.set(card, { opacity: 0, y: 40 });
    gsap.set(animItems, { opacity: 0, y: 20 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;
          const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
          tl.to(card, { opacity: 1, y: 0, duration: 0.6 });
          if (animItems.length) {
            tl.to(animItems, { opacity: 1, y: 0, duration: 0.5, stagger: 0.06 }, '-=0.25');
          }
          observer.disconnect();
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(sectionRef.current || card);
    return () => observer.disconnect();
  }, []);

  if (!data) return null;

  const effectiveFormValues = fields.reduce((accumulator, field) => {
    const key = typeof field?.name === 'string' ? field.name.trim() : '';

    if (!key) return accumulator;

    accumulator[key] = field?.type === 'items' && lockedUnidadValue
      ? lockedUnidadValue
      : formValues[key] ?? '';

    return accumulator;
  }, {});

  /* ─── Handlers ─── */
  const handleChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;

      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);

    const formElement = formRef.current || e.currentTarget;

    const validationMessages = {
      errorRequired: data?.errorRequired,
      errorEmail: data?.errorEmail,
    };

    const validationErrors = validateFields(fields, effectiveFormValues, validationMessages);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setToast({
        type: 'error',
        message: data?.errorFormIncomplete || '',
      });
      setTimeout(() => setToast(null), 4000);

      const firstInvalidField = fields.find((field) => validationErrors[field?.name]);
      if (firstInvalidField?.name && formElement) {
        const invalidControl = formElement.querySelector(`[name="${firstInvalidField.name}"]`);
        invalidControl?.focus();
      }

      return;
    }

    setFieldErrors({});

    const missingRequiredField = getFirstMissingRequiredField(fields, effectiveFormValues);
    if (missingRequiredField) {
      const template = data?.errorMissingField;
      const label = missingRequiredField.label;
      if (template) {
        setToast({
          type: 'error',
          message: template.replace('{label}', label),
        });
        setTimeout(() => setToast(null), 4000);
      }
      return;
    }

    setSubmitting(true);

    const normalizeText = (value = '') => value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

    const fieldEntries = fields
      .map((field) => {
        const key = typeof field?.name === 'string' ? field.name.trim() : '';

        return {
          field,
          key,
          value: key ? effectiveFormValues[key] ?? '' : '',
          meta: normalizeText(`${field?.label || ''} ${field?.placeholder || ''}`),
        };
      })
      .filter(({ key }) => Boolean(key));

    const leadData = fieldEntries.reduce((acc, entry) => {
      acc[entry.key] = entry.value;
      return acc;
    }, {});

    const firstMatchingEntry = (predicate) => fieldEntries.find((entry) => entry.value && predicate(entry));
    const textEntries = fieldEntries.filter(({ field, value }) => field?.type === 'text' && value);

    const fullName =
      firstMatchingEntry(({ field, meta }) => field?.type === 'text' && /(nombre completo|full name|nombre|name)/.test(meta))?.value
      || (textEntries.length === 1 ? textEntries[0].value : '');

    const email = firstMatchingEntry(({ field }) => field?.type === 'email')?.value || '';
    const rawPhone = firstMatchingEntry(({ field }) => field?.type === 'phone')?.value || '';
    const phone = rawPhone ? `${phoneCountry.code} ${rawPhone}` : '';
    const country =
      firstMatchingEntry(({ meta }) => /(pais|country|nacionalidad)/.test(meta))?.value
      || '';
    const unitInterest =
      firstMatchingEntry(({ field, meta }) => field?.type === 'items' || /(unidad de interes|unidad interes|unit of interest|unidad|producto|product|propiedad de interes|modelo de interes)/.test(meta))?.value
      || '';
    const presupuestoRaw =
      firstMatchingEntry(({ meta }) => /(presupuesto|budget|rango de inversion|inversion|valor estimado|estimated value)/.test(meta))?.value
      || '';
    const estimatedValue = parseLeadEstimatedValue(presupuestoRaw);

    try {
      await submitLead({
        fullName,
        email,
        phone,
        country,
        source: source || window.location.pathname,
        projectId: derivedProjectId,
        IdProducto: unitInterest,
        estimatedValue,
        leadType: 'formulario',
        data: leadData,
      });

      // Reset form values
      const reset = {};
      fields.forEach((f) => {
        reset[f.name] = '';
      });
      setFormValues(reset);
      setFieldErrors({});

      const hasThankYouContent = data?.thankYou && data.thankYou.length > 0;

      if (hasThankYouContent && formRef.current) {
        // Make form invisible (keep in DOM for height)
        gsap.to(formRef.current, {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            setShowThankYou(true);
            requestAnimationFrame(() => {
              if (thankYouRef.current) {
                gsap.fromTo(thankYouRef.current,
                  { opacity: 0, y: 20 },
                  {
                    opacity: 1, y: 0, duration: 0.5, ease: 'power3.out',
                    onComplete: () => {
                      setTimeout(() => {
                        if (thankYouRef.current) {
                          gsap.to(thankYouRef.current, {
                            opacity: 0,
                            y: -10,
                            duration: 0.4,
                            ease: 'power2.in',
                            onComplete: () => {
                              setShowThankYou(false);
                              // Restore form visibility
                              if (formRef.current) {
                                gsap.fromTo(formRef.current,
                                  { opacity: 0 },
                                  { opacity: 1, duration: 0.5, ease: 'power3.out' }
                                );
                              }
                            },
                          });
                        }
                      }, 4000);
                    },
                  }
                );
              }
            });
          },
        });
      } else {
        // No thankYou content: show checkmark overlay on form
        setShowSuccessOverlay(true);
        requestAnimationFrame(() => {
          if (successOverlayRef.current) {
            gsap.fromTo(successOverlayRef.current,
              { opacity: 0 },
              {
                opacity: 1, duration: 0.4, ease: 'power2.out',
                onComplete: () => {
                  setTimeout(() => {
                    if (successOverlayRef.current) {
                      gsap.to(successOverlayRef.current, {
                        opacity: 0, duration: 0.4, ease: 'power2.in',
                        onComplete: () => setShowSuccessOverlay(false),
                      });
                    }
                  }, 2500);
                },
              }
            );
          }
        });
      }
    } catch {
      setToast({ type: 'error', message: data?.errorSubmit || '' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── Content blocks (left side) ─── */
  const renderContentBlock = (block, index) => {
    if (!block) return null;

    switch (block.__component) {
      case 'shared.heading': {
        const level = block.level || 'h2';
        const Tag = level;
        const headingStyle = getHeadingFontStyle(
          block?.fontSize,
          level,
          CONTACT_FORM_HEADING_FONT_SIZE_FALLBACKS
        );
        const cls = [
          styles.contactForm__heading,
          styles[`contactForm__heading--${level}`],
          styles.contactForm__animateItem,
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <Tag key={`h-${index}`} className={cls} style={headingStyle}>
            {block.text}
          </Tag>
        );
      }
      case 'shared.paragraph': {
        const paragraphStyle = getContentFontStyle(block?.fontSize, CONTACT_FORM_PARAGRAPH_FONT_SIZE);
        return (
          <p
            key={`p-${index}`}
            className={`${styles.contactForm__text} ${styles.contactForm__animateItem}`}
            style={paragraphStyle}
          >
            {block.text}
          </p>
        );
      }
      case 'shared.list': {
        const listItemStyle = getContentFontStyle(block?.fontSize, CONTACT_FORM_LIST_FONT_SIZE);
        const hasIcon = block.icon?.url;
        const iconMedia = hasIcon
          ? getStrapiMediaAttributes(block.icon, {
            altFallback: getIconFallbackLabel(locale, 'list'),
            titleFallback: getIconFallbackLabel(locale, 'list'),
          })
          : null;
        const ListTag = !hasIcon && block.ordered ? 'ol' : 'ul';
        const listCls = [
          styles.contactForm__list,
          hasIcon
            ? styles['contactForm__list--icons']
            : block.ordered
              ? styles['contactForm__list--ordered']
              : styles['contactForm__list--unordered'],
          styles.contactForm__animateItem,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <ListTag key={`list-${index}`} className={listCls}>
            {block.items?.map((item, i) => (
              <li key={`li-${i}`} className={styles.contactForm__listItem} style={listItemStyle}>
                {iconMedia?.src && (
                  <img
                    className={styles.contactForm__listIcon}
                    src={iconMedia.src}
                    alt={iconMedia.alt}
                    title={iconMedia.title}
                    aria-hidden="true"
                  />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ListTag>
        );
      }
      default:
        return null;
    }
  };

  /* ─── Form fields (right side) ─── */
  const renderField = (field) => {
    const value = effectiveFormValues[field.name] ?? '';
    const hasError = Boolean(fieldErrors[field.name]);

    switch (field.type) {
      case 'text':
      case 'number':
        {
          const isCountryField = /^(pa[ií]s|country)$/i.test(field.name) || /^(pa[ií]s|country)$/i.test(field.label);
          const opts = field.options?.length > 0
            ? field.options.map((opt) => ({ label: opt.label, value: opt.value }))
            : isCountryField
              ? localizedCountryOptions
              : null;

          if (opts) {
            return (
              <select
                className={`${styles.contactForm__select} ${hasError ? styles['contactForm__select--error'] : ''}`}
                name={field.name}
                value={value}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
              >
                <option value="">{field.placeholder || field.label}</option>
                {opts.map((opt, i) => (
                  <option key={`opt-${i}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            );
          }
          return (
            <input
              className={`${styles.contactForm__input} ${hasError ? styles['contactForm__input--error'] : ''}`}
              type={field.type}
              name={field.name}
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={field.required}
            />
          );
        }

      case 'email':
        return (
          <input
            className={`${styles.contactForm__input} ${hasError ? styles['contactForm__input--error'] : ''}`}
            type="email"
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
          />
        );

      case 'phone':
        return (
          <div className={`${styles.contactForm__phoneWrapper} ${hasError ? styles['contactForm__phoneWrapper--error'] : ''}`}>
            <div className={styles.contactForm__phoneCode}>
              <select
                value={phoneCountry.code}
                onChange={(e) => {
                  const found = COUNTRY_CODES.find((c) => c.code === e.target.value);
                  if (found) setPhoneCountry(found);
                }}
                aria-label={phoneCountryAriaLabel}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: 'inherit',
                  padding: 0,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
            </div>
            <input
              className={styles.contactForm__phoneInput}
              type="tel"
              name={field.name}
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={field.required}
            />
          </div>
        );

      case 'select': {
        const isCountryField = /^(pa[ií]s|country)$/i.test(field.name) || /^(pa[ií]s|country)$/i.test(field.label);
        const selectOptions = field.options?.length > 0
          ? field.options.map((opt) => ({ label: opt.label, value: opt.value }))
          : isCountryField
            ? localizedCountryOptions
            : [];
        return (
          <select
            className={`${styles.contactForm__select} ${hasError ? styles['contactForm__select--error'] : ''}`}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
          >
            <option value="">{field.placeholder || field.label}</option>
            {selectOptions.map((opt, i) => (
              <option key={`opt-${i}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      case 'chips':
        return (
          <div className={`${styles.contactForm__chips} ${hasError ? styles['contactForm__chips--error'] : ''}`}>
            {field.options?.map((opt, i) => {
              const isActive = value === opt.value;
              return (
                <button
                  key={`chip-${i}`}
                  type="button"
                  className={`${styles.contactForm__chip} ${isActive ? styles['contactForm__chip--active'] : ''}`}
                  onClick={() => handleChange(field.name, isActive ? '' : opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            className={`${styles.contactForm__textarea} ${hasError ? styles['contactForm__textarea--error'] : ''}`}
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
            rows={4}
          />
        );

      case 'items': {
        // If URL param provided, show locked read-only field
        if (lockedUnidadValue) {
          return (
            <input
              className={`${styles.contactForm__input} ${hasError ? styles['contactForm__input--error'] : ''}`}
              type="text"
              name={field.name}
              value={lockedUnidadValue}
              readOnly
              disabled
            />
          );
        }
        const itemOptions = items.length > 0
          ? items.map((item) => ({ label: item.titulo, value: item.titulo }))
          : (field.options || []).map((opt) => ({ label: opt.label, value: opt.value }));
        return (
          <select
            className={`${styles.contactForm__select} ${hasError ? styles['contactForm__select--error'] : ''}`}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
          >
            <option value="">{field.placeholder || field.label}</option>
            {itemOptions.map((opt, i) => (
              <option key={`item-${i}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      default:
        return null;
    }
  };

  // Group fields: consecutive halfWidth fields go into rows of 2
  const groupedFields = [];
  let i = 0;
  while (i < fields.length) {
    if (fields[i].halfWidth && i + 1 < fields.length && fields[i + 1].halfWidth) {
      groupedFields.push({ type: 'row', fields: [fields[i], fields[i + 1]] });
      i += 2;
    } else {
      groupedFields.push({ type: 'single', field: fields[i] });
      i += 1;
    }
  }

  const contentBlocks = (data?.content || []).filter(hasRenderableContentBlock);
  const hasContent = contentBlocks.length > 0;

  const rootClassName = [
    styles.contactForm,
    hasHero ? styles['contactForm--hasHero'] : styles['contactForm--noHero'],
    !hasContent ? styles['contactForm--noContent'] : null,
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedTermsLink = resolveLegalLink(data.termsUrl, legalRouteMap);
  const resolvedPrivacyLink = resolveLegalLink(data.privacyUrl, legalRouteMap);
  const termsText = typeof data.termsText === 'string' ? data.termsText.trim() : '';
  const termsLabel = typeof data.termsLabel === 'string' ? data.termsLabel.trim() : '';
  const privacyLabel = typeof data.privacyLabel === 'string' ? data.privacyLabel.trim() : '';
  const showTermsLink = Boolean(termsLabel) && resolvedTermsLink.mode !== 'empty';
  const showPrivacyLink = Boolean(privacyLabel) && resolvedPrivacyLink.mode !== 'empty';
  const showLegalNotice = Boolean(termsText) || showTermsLink || showPrivacyLink;

  return (
    <section ref={sectionRef} className={rootClassName}>
      {/* Hero background header */}
      {hasHero && (
        <div
          className={styles.contactForm__hero}
          style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
        >
          {backgroundImage && <div className={styles.contactForm__heroOverlay} />}
          {data.overlayTitle && (
            <p className={`${styles.contactForm__heroTitle} ${styles.contactForm__animateItem}`}>
              {data.overlayTitle}
            </p>
          )}
          {data.overlaySubtitle && (
            <h2 className={`${styles.contactForm__heroSubtitle} ${styles.contactForm__animateItem}`}>
              {data.overlaySubtitle}
            </h2>
          )}
        </div>
      )}

      {/* Card with content + form */}
      <div ref={cardRef} className={styles.contactForm__card}>
        {/* Left column: descriptive content */}
        {hasContent && (
          <div className={styles.contactForm__content}>
            {contentBlocks.map((block, idx) => renderContentBlock(block, idx))}
          </div>
        )}

        {/* Right column: form + overlays */}
        <div className={styles.contactForm__formWrapper}>
          {showSuccessOverlay && (
            <div ref={successOverlayRef} className={styles.contactForm__successOverlay}>
              <svg className={styles.contactForm__successIcon} viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="26" cy="26" r="25" stroke="#4CAF50" strokeWidth="2" />
                <path d="M15 27l7 7 15-15" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          {showThankYou && (
            <div ref={thankYouRef} className={styles.contactForm__thankYou}>
              {data.thankYou?.map((block, idx) => renderContentBlock(block, idx))}
            </div>
          )}
          <form ref={formRef} className={styles.contactForm__form} onSubmit={handleSubmit} noValidate>
          {groupedFields.map((group, gIdx) => {
            if (group.type === 'row') {
              return (
                <div key={`row-${gIdx}`} className={styles.contactForm__fieldRow}>
                  {group.fields.map((field) => (
                    <div
                      key={field.name}
                      className={`${styles.contactForm__fieldGroup} ${styles['contactForm__fieldGroup--half']} ${styles.contactForm__animateItem}`}
                    >
                      <label className={styles.contactForm__label}>{field.label}</label>
                      {renderField(field)}
                      {fieldErrors[field.name] && (
                        <p className={styles.contactForm__fieldError}>{fieldErrors[field.name]}</p>
                      )}
                    </div>
                  ))}
                </div>
              );
            }

            const field = group.field;
            return (
              <div
                key={field.name}
                className={`${styles.contactForm__fieldGroup} ${styles['contactForm__fieldGroup--full']} ${styles.contactForm__animateItem}`}
              >
                <label className={styles.contactForm__label}>{field.label}</label>
                {renderField(field)}
                {fieldErrors[field.name] && (
                  <p className={styles.contactForm__fieldError}>{fieldErrors[field.name]}</p>
                )}
              </div>
            );
          })}

          {/* Footer: Terms + Submit */}
          <div className={styles.contactForm__footer}>
            {showLegalNotice && (
              <p className={styles.contactForm__terms}>
                {termsText}
                {showTermsLink && (
                  <>
                    {termsText ? ' ' : ''}
                    <a
                      className={styles.contactForm__termsLink}
                      href={resolvedTermsLink.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={termsLabel}
                    >
                      {termsLabel}
                    </a>
                  </>
                )}
                {showPrivacyLink && (
                  <>
                    {termsText || showTermsLink ? ' ' : ''}
                    <a
                      className={styles.contactForm__termsLink}
                      href={resolvedPrivacyLink.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={privacyLabel}
                    >
                      {privacyLabel}
                    </a>
                  </>
                )}
              </p>
            )}

            <button
              type="submit"
              className={styles.contactForm__submit}
              disabled={submitting}
            >
              {submitting ? getUiText(locale, 'sending') : data.submitLabel || getUiText(locale, 'submit')}
              {!submitting && <span className={styles.contactForm__submitArrow}>→</span>}
            </button>
          </div>
            </form>
          </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          className={`${styles.contactForm__toast} ${
            toast.type === 'success'
              ? styles['contactForm__toast--success']
              : styles['contactForm__toast--error']
          }`}
        >
          {toast.message}
        </div>
      )}
    </section>
  );
};

export default ContactForm;
